import { publicApi } from "@api";
import ContactLinksDialog from "@components/layouts/ContactLinksDialog";
import AppInstaller from "@components/utils/AppInstaller";
import AppVersion from "@components/utils/AppVersion";
import OnboardingDialog from "@components/utils/OnboardingDialog";
import { OnboardingDialogUrlTrigger } from "@components/utils/OnboardingDialog/components/DialogUrlTrigger";
import { envConfig } from "@configs/env";
import { Paths, SEARCH_PARAMS } from "@constants";
import { DeployEnv } from "@types";
import { storeReferralCode } from "@utils/referral";
import { PageTransitionOverlay, cn } from "kz-ui-sdk";
import { PropsWithChildren, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

interface RootRouteLayoutProps extends PropsWithChildren {}

const PRIVATE_PATHS = Object.values(Paths.PRIVATE);

const RootRouteLayout = ({ children }: RootRouteLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: serverConfig } = publicApi.useGetServerConfigQuery(
    {
      group: envConfig.brand.group,
      accountId: envConfig.accountId,
      env: envConfig.deploymentEnv,
    },
    {
      skip: envConfig.deploymentEnv === DeployEnv.Local,
    },
  );

  const overlayClassName = useMemo(() => {
    if (PRIVATE_PATHS.includes(location.pathname)) {
      return "z-[5]";
    }
    return "z-[1030]";
  }, [location.pathname]);

  const underMaintenance = useMemo(
    () => serverConfig?.underMaintenance ?? import.meta.env.VITE_MAINTENANCE === "true",
    [serverConfig],
  );

  //Redirect to maintenance if server config is maintenance
  useEffect(() => {
    if (underMaintenance) {
      navigate(Paths.PUBLIC.MAINTENANCE, {
        replace: true,
      });
    }
  }, [underMaintenance, navigate, serverConfig]);

  // Capture referral code from any URL
  useEffect(() => {
    const refCode = searchParams.get(SEARCH_PARAMS.REF_CODE);
    storeReferralCode(refCode);
  }, [searchParams]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // if ((isFetching || underMaintenance) && !matchPath(Paths.PUBLIC.MAINTENANCE, location.pathname)) return null;

  return (
    <>
      <OnboardingDialogUrlTrigger />
      <div className={cn("w-screen max-w-[100vw]")}>
        <AppInstaller />
        <AppVersion />
        <PageTransitionOverlay
          className={cn(overlayClassName)}
          key={location.pathname}
        />
        {children}
      </div>
      <ContactLinksDialog />
      <OnboardingDialog />
    </>
  );
};

export default RootRouteLayout;
